import {css, StyleSheet} from 'aphrodite';
import classnames from 'classnames';
import gql from 'graphql-tag';
import React from 'react';

import {ANALYSIS_TOOL_SPACING} from 'analysis-tool/AnalysisToolConstants';
import {findAndAssertSingleStepDataFrameId} from 'analysis-tool/data/findAndAssertStepDataFrameIds';
import {AnalysisMainViewItemType, MainViewComponentProps} from 'analysis-tool/ui/main-view/MainViewTypes';
import {assertAndContinue} from 'Asserts';
import {DataFrame} from 'data-frames/DataFrameTypes';
import {DeprecatedDataFrameFragment} from 'data-frames/graphql/GetDataFrame';
import BenchlingQuery from 'graphql-client/BenchlingQuery';
import {getFragmentWrapper} from 'graphql-client/declarative-translations/external/getFragmentWrapper';
import {isInaccessible} from 'graphql-client/InaccessibleInfo';
import {VisualizationType} from 'insights/analytics-visualizations/AnalyticsVisualizationTypes';
import makeVisualizationConfig from 'insights/universal-blocks/makeVisualizationConfig';
import {
  GetParameterOptimizationDatasetConfigurationOutputsQuery,
  GetParameterOptimizationDatasetConfigurationOutputsQueryVariables,
} from 'machine-learning/parameter-optimization/legacy-components/__generated/GetParameterOptimizationDatasetConfigurationOutputsQuery';
import ModelingDashboardBlock from 'machine-learning/parameter-optimization/legacy-components/modeling-dashboard/ModelingDashboardBlock';
import {
  FEATURE_DESCRIPTIONS_DATA_FRAME_NAME,
  FEATURE_DATA_DATA_FRAME_NAME,
  LABEL_DESCRIPTIONS_DATA_FRAME_NAME,
  LABEL_DATA_DATA_FRAME_NAME,
} from 'machine-learning/parameter-optimization/legacy-components/ParameterOptimizationConstants';
import ErrorBanner from 'ui-library/banners/ErrorBanner';
import makeUseGlobalClientState from 'ui-library/hooks/global-client-state/makeUseGlobalClientState';
import {CenteredHorizontalThrobber} from 'ui-library/HorizontalThrobber';

export type ParameterOptimizationDatasetConfigurationDashboardViewProps =
  MainViewComponentProps<AnalysisMainViewItemType.PARAMETER_OPTIMIZATION_DATASET_CONFIGURATION_DASHBOARD>;

interface DashboardDataFrames {
  featureData: DataFrame;
  featureDescriptions: DataFrame;
  labelData: DataFrame;
  labelDescriptions: DataFrame;
}

const FEATURE_DATA_TITLE = 'Parameter data';
const FEATURE_DESCRIPTIONS_TITLE = 'Parameter summary statistics';
const LABEL_DATA_TITLE = 'Output data';
const LABEL_DESCRIPTIONS_TITLE = 'Output summary statistics';
const ALL_BLOCK_KEYS = [
  FEATURE_DESCRIPTIONS_TITLE,
  LABEL_DESCRIPTIONS_TITLE,
  FEATURE_DATA_TITLE,
  LABEL_DATA_TITLE,
];
type AllBlockKeys = (typeof ALL_BLOCK_KEYS)[number];

const expandedBlockState = makeUseGlobalClientState<AllBlockKeys | null>(null);

function ParameterOptimizationDatasetConfigurationDashboardView({
  clientStateId,
  data: {featureData, featureDescriptions, labelData, labelDescriptions},
  step: {id: stepId},
}: ParameterOptimizationDatasetConfigurationDashboardViewProps & {data: DashboardDataFrames}): JSX.Element {
  const [expandedBlock, setExpandedBlock] = expandedBlockState.useGlobalClientState(stepId);

  const blockKeyToDataFrame: Record<AllBlockKeys, DataFrame> = {
    [FEATURE_DESCRIPTIONS_TITLE]: featureDescriptions,
    [LABEL_DESCRIPTIONS_TITLE]: labelDescriptions,
    [FEATURE_DATA_TITLE]: featureData,
    [LABEL_DATA_TITLE]: labelData,
  };
  const blocksMap = Object.fromEntries(
    ALL_BLOCK_KEYS.map((blockKey) => [
      blockKey,
      <ModelingDashboardBlock
        dashboardClientStateId={clientStateId}
        isExpanded={expandedBlock === blockKey}
        onToggleExpand={(newIsExpanded) => {
          setExpandedBlock(newIsExpanded ? blockKey : null);
        }}
        inputDataFrame={blockKeyToDataFrame[blockKey]}
        visualizationConfig={makeVisualizationConfig(VisualizationType.TABLE, {
          displayColumnsJson: null,
        })}
        blockTitle={blockKey}
        blockDescription=""
      />,
    ])
  );

  return (
    <div className={css(styles.wrapper)}>
      This dashboard includes data about the parameters and outputs that were used when building the model.
      {expandedBlock != null ? (
        <div className="u-small-spacing-top">{blocksMap[expandedBlock]}</div>
      ) : (
        <div className={css(styles.grid)}>
          {Object.entries(blocksMap).map(([blockKey, block]) => (
            <div className={classnames('u-small-spacing-top', css(styles.block))} key={blockKey}>
              {block}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

export const GET_PARAMETER_OPTIMIZATION_DATASET_CONFIGURATION_DATA_FRAMES_QUERY = gql`
  query GetParameterOptimizationDatasetConfigurationOutputsQuery(
    $featureDataId: StrictString!
    $featureDescriptionsId: StrictString!
    $labelDataId: StrictString!
    $labelDescriptionsId: StrictString!
  ) @skipAudit @engComponent(name: MACHINE_LEARNING) {
    featureData: dataFrame(id: $featureDataId) {
      ... on DataFrameNode {
        ...DataFrameGraphQL
      }
      ...InaccessibleInfoGraphQL
    }
    featureDescriptions: dataFrame(id: $featureDescriptionsId) {
      ... on DataFrameNode {
        ...DataFrameGraphQL
      }
      ...InaccessibleInfoGraphQL
    }
    labelData: dataFrame(id: $labelDataId) {
      ... on DataFrameNode {
        ...DataFrameGraphQL
      }
      ...InaccessibleInfoGraphQL
    }
    labelDescriptions: dataFrame(id: $labelDescriptionsId) {
      ... on DataFrameNode {
        ...DataFrameGraphQL
      }
      ...InaccessibleInfoGraphQL
    }
  }
  ${DeprecatedDataFrameFragment.fragment}
  ${getFragmentWrapper('InaccessibleInfo').fragment}
`;

export default function QueryingParameterOptimizationDatasetConfigurationDashboardView(
  props: ParameterOptimizationDatasetConfigurationDashboardViewProps
): JSX.Element {
  const {step} = props;
  return (
    <BenchlingQuery<
      GetParameterOptimizationDatasetConfigurationOutputsQuery,
      GetParameterOptimizationDatasetConfigurationOutputsQueryVariables
    >
      query={GET_PARAMETER_OPTIMIZATION_DATASET_CONFIGURATION_DATA_FRAMES_QUERY}
      renderLoading={() => <CenteredHorizontalThrobber />}
      variables={{
        featureDataId: findAndAssertSingleStepDataFrameId(step, FEATURE_DATA_DATA_FRAME_NAME),
        featureDescriptionsId: findAndAssertSingleStepDataFrameId(step, FEATURE_DESCRIPTIONS_DATA_FRAME_NAME),
        labelDataId: findAndAssertSingleStepDataFrameId(step, LABEL_DATA_DATA_FRAME_NAME),
        labelDescriptionsId: findAndAssertSingleStepDataFrameId(step, LABEL_DESCRIPTIONS_DATA_FRAME_NAME),
      }}
    >
      {(data) => {
        if (
          isInaccessible(data.featureData) ||
          isInaccessible(data.featureDescriptions) ||
          isInaccessible(data.labelData) ||
          isInaccessible(data.labelDescriptions)
        ) {
          assertAndContinue(false, 'Got inaccessible dataframe with an analysis', {
            featureData: isInaccessible(data.featureData) ? data.featureData.invalidId : data.featureData.id,
            featureDescriptionsId: isInaccessible(data.featureDescriptions)
              ? data.featureDescriptions.invalidId
              : data.featureDescriptions.id,
            labelDataId: isInaccessible(data.labelData) ? data.labelData.invalidId : data.labelData.id,
            labelDescriptions: isInaccessible(data.labelDescriptions)
              ? data.labelDescriptions.invalidId
              : data.labelDescriptions.id,
          });
          return <ErrorBanner>You do not have access to the datasets for this dashboard.</ErrorBanner>;
        }
        return (
          <ParameterOptimizationDatasetConfigurationDashboardView
            {...props}
            data={{
              featureData: data.featureData.dataFrameJson,
              featureDescriptions: data.featureDescriptions.dataFrameJson,
              labelData: data.labelData.dataFrameJson,
              labelDescriptions: data.labelDescriptions.dataFrameJson,
            }}
          />
        );
      }}
    </BenchlingQuery>
  );
}

const styles = StyleSheet.create({
  wrapper: {
    margin: ANALYSIS_TOOL_SPACING,
  },
  grid: {
    display: 'grid',
    gap: ANALYSIS_TOOL_SPACING,
    gridTemplateColumns: '100%',
  },
  block: {
    maxHeight: 400,
  },
});
